/**
 * CEPI's capability handoff card.
 *
 * Appears under an answer when the ANSWERING MODEL judged that Cogni, Keyword
 * or Investigator would serve the request better than a chat turn can. It is an
 * offer, never a wall: CEPI has already answered above this card, so `Dismiss`
 * costs nothing and no second turn is spent. That is why there is no "just
 * answer it anyway" button — the answer is already there.
 *
 * The buttons differ only in WHO DRIVES:
 *
 *   Run it for me       CEPI owns execution. The job runs in the background and
 *                       the user keeps working; when it lands, CEPI offers
 *                       to answer from its results (never attached by itself).
 *   Open …              The user owns setup, on the capability's own surface,
 *                       where its Document Type / Region / Application Type
 *                       filters already live.
 *
 * Only Investigator can be run for the user today: its endpoint is the only one
 * that produces an addressable result CEPI can read back afterwards. Running
 * Cogni or Keyword on the user's behalf would produce work the conversation is
 * blind to — the exact fork this feature exists to close. That is an
 * implementation limit, not a product one, so they are still recommended.
 *
 * The scope summary sits BEFORE the buttons whenever a run spends real compute
 * over N products. A user should never start a cohort run without having seen
 * the cohort.
 */
import React from 'react';
import type { Capability, CapabilityAction } from '../../types/qa';
import type { JobProgress } from '../../services/watchedJobs';
import { runProgressView } from '../../services/watchedJobs';

const CAPABILITY_LABEL: Record<Capability, string> = {
  cogni: 'Cogni',
  keyword: 'Keyword',
  investigator: 'Investigator',
};

/**
 * What running this DOES to the conversation's working set.
 *
 * Two ordinary operations, stated in the user's terms. Every research tool
 * has both, and the user already knows the difference — the previous version
 * of this line described one of them as a limitation ("does not inherit your
 * attached results"), which is why it read as a bug rather than a choice.
 *
 * Nothing is suppressed on either side: a user who ran Cogni once must not be
 * locked out of Keyword for the rest of the conversation.
 */
const OPERATION_LABEL = {
  narrow: (n: number) => n > 0
    ? `Narrow these ${n} — a verdict for each product`
    : 'Narrow your attached results — a verdict for each product',
  new_search: () => 'New search — across the full corpus',
} as const;

/** What each capability can actually promise. Kept next to the label so the
 *  three epistemic guarantees never blur into one "recommended" chip. */
const CAPABILITY_BLURB: Record<Capability, string> = {
  cogni: 'Ranked search by meaning across the corpus',
  keyword: 'Exact literal and Boolean search — can prove absence',
  investigator: 'Deep research: a verdict for each product',
};

/** A launched Investigator run, as the card shows it (R6). Absent = the offer. */
export interface CapabilityRunView {
  phase: 'starting' | 'running' | 'stopped' | 'failed';
  progress: JobProgress | null;
  pinned: boolean;
  /** member_drug_ids.length (the card's N fallback). */
  pinnedCount: number;
  /** Absent until the job id is known (R6.4). */
  onGoToTab?: (() => void) | undefined;
  /** Absent until the job id is known; ignored outside starting/running. */
  onStop?: (() => void) | undefined;
  stop: 'idle' | 'stopping' | 'ended' | 'error';
}

const RUN_EYEBROW: Record<CapabilityRunView['phase'], string> = {
  starting: 'STARTING',
  running: 'RUNNING IN A NEW TAB',
  stopped: 'STOPPED',
  failed: 'FAILED',
};

const RUN_FOOTNOTE: Record<CapabilityRunView['phase'], string> = {
  starting: "I'll post here when it's done.",
  running: "I'll post here when it's done.",
  stopped: 'Stopped. Matches found so far stay in the tab.',
  failed: 'The run failed before finishing.',
};

interface CapabilityCardProps {
  action: CapabilityAction;
  /** Start the run as-is. Omit to hide the button (e.g. not yet wired). */
  onRun?: ((action: CapabilityAction) => void) | undefined;
  /** Open the capability's own surface, pre-filled from `scope_draft`. */
  onOpen?: ((action: CapabilityAction) => void) | undefined;
  /** This card has spent its offer. Permanent: one card launches one job. */
  busy?: boolean | undefined;
  /** The launched run's state (R6). Replaces the offer while present. */
  run?: CapabilityRunView | undefined;
  /** Shell: this recommendation was opened in a tab for the user (D14). The
   *  Open button gives way to a link that focuses that tab. */
  opened?: boolean | undefined;
  onFocus?: ((action: CapabilityAction) => void) | undefined;
  /** Shell: open CEPI Agents in a tab. The nudge is hidden when absent. */
  onOpenAgents?: (() => void) | undefined;
  /** The launched Investigator run completed. Replaces the run button with the
   *  explicit offer to answer from its results (never auto-attached). */
  done?: { matched: number; onAnswer: () => void; onOpenTab?: (() => void) | undefined } | undefined;
  /** Disables the answer control while another scope change is in flight. */
  doneBusy?: boolean | undefined;
}

export const CapabilityCard: React.FC<CapabilityCardProps> = ({
  action, onRun, onOpen, busy = false, run,
  opened = false, onFocus, onOpenAgents, done, doneBusy = false,
}) => {
  // Dismissal is local and permanent for this message. Not persisted: the card
  // rides a single answer, and a dismissed offer should not follow the user
  // into a new conversation.
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;

  const { scope_draft: scope, options, capability } = action;
  const label = CAPABILITY_LABEL[capability] ?? capability;
  if (run && !done) return <RunCard action={action} label={label} run={run} onDismiss={() => setDismissed(true)} />;
  const canRun = options.includes('run') && !!onRun;
  const canOpen = options.includes('open') && !!onOpen && !opened;
  // Nothing the user can act on means nothing worth rendering: a card whose
  // only control is Dismiss is an interruption with no offer behind it. In
  // practice the backend always sends at least `open`, so this is a
  // wiring-mistake guard, not a live case.
  if (!canRun && !canOpen && !opened) return null;
  const pinned = scope.coverage_mode === 'complete' && scope.member_count > 0;

  return (
    <div
      className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3"
      data-testid="capability-card"
      data-capability={capability}
      data-coverage={scope.coverage_mode}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
          <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor"
               viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>{done ? `${label.toUpperCase()} · DONE` : `${label} recommended`}</span>
        </div>
        {/* Always present, on every capability. A recommendation must never
            read as a requirement. */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="capability-dismiss"
          aria-label={`Dismiss the ${label} suggestion`}
          className="-mr-1 -mt-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-white/70 hover:text-slate-700"
        >
          Dismiss
        </button>
      </div>

      <p className="mt-1.5 text-sm text-slate-700">{action.reason}</p>

      <dl className="mt-2.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        {/* The cohort, before the buttons — a pinned run spends compute over
            every member, so the count is not a detail. Shown ONLY when pinned:
            printing "Products: 0" on a discovery run reads as a broken scope
            rather than the capability's normal mode. */}
        {pinned && (
          <>
            <dt className="text-slate-500">Products</dt>
            <dd className="font-medium tabular-nums text-slate-800">
              {scope.member_count}
            </dd>
          </>
        )}
        <dt className="text-slate-500">Scope</dt>
        <dd className="text-slate-800" data-testid="capability-scope">
          {pinned
            ? 'Every product in your results gets a verdict'
            : CAPABILITY_BLURB[capability]}
        </dd>
        <dt className="text-slate-500">
          {capability === 'keyword' ? 'Terms' : 'Criterion'}
        </dt>
        <dd className="text-slate-800 break-words">{scope.criterion}</dd>
      </dl>

      {/* What this run does to the working set. Shown only when there IS one —
          with nothing attached there is no narrow-vs-new distinction to draw,
          and printing one invents a contrast the user cannot act on. */}
      {action.operation && (
        <p className="mt-2 text-[11px] font-medium text-slate-600"
           data-testid="capability-operation"
           data-operation={action.operation}>
          {action.operation === 'narrow'
            ? OPERATION_LABEL.narrow(scope.member_count)
            : OPERATION_LABEL.new_search()}
        </p>
      )}

      {/* After a few hand-driven steps, point at the tool built for the job.
          A NUDGE, never a gate: the card below still works, at this hop and
          every hop after it. Chaining never becomes what CEPI Agents does —
          an approved multi-step plan that runs autonomously — so capping it
          would remove a working capability to prevent a thing that cannot
          happen. What is true is that doing it by hand gets tedious. */}
      {action.agents_nudge && onOpenAgents && (
        <p className="mt-2 text-[11px] text-slate-500"
           data-testid="capability-agents-nudge">
          Doing this in several steps?{' '}
          <button
            type="button"
            onClick={onOpenAgents}
            className="font-medium text-indigo-700 underline"
          >
            Agentic reporting
          </button>{' '}
          can run a scoped plan end to end.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
          {done && (
            <>
              {done.matched > 0 && (
                <button
                  type="button"
                  onClick={done.onAnswer}
                  disabled={doneBusy}
                  data-testid="capability-answer"
                  className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Answer from these {done.matched} results
                </button>
              )}
              {done.onOpenTab && (
                <button
                  type="button"
                  onClick={done.onOpenTab}
                  data-testid="capability-open-results"
                  className="rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  Open results tab
                </button>
              )}
            </>
          )}
          {canRun && !done && (
            <button
              type="button"
              onClick={() => onRun!(action)}
              disabled={busy}
              data-testid="capability-run"
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Run it for me
            </button>
          )}
          {opened && (
            <span className="text-xs text-slate-700">
              Opened in a tab
              {onFocus && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => onFocus(action)}
                    data-testid="capability-focus"
                    className="font-medium text-indigo-700 underline"
                  >
                    Show it
                  </button>
                </>
              )}
            </span>
          )}
          {canOpen && (
            <button
              type="button"
              onClick={() => onOpen!(action)}
              disabled={busy}
              data-testid="capability-open"
              className="rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Open {label}
            </button>
          )}
          {/* Says what "Run it for me" actually does. Measured on prod
              2026-09-23 across seven pinned runs of 179 products: mean 946s,
              i.e. ~5s per product (the comment here previously guessed
              30-100s, which was 6-19x too high). A 179-product cohort is
              still ~15 minutes, so "background" remains the difference
              between a usable affordance and one the user thinks has hung. */}
          {canRun && !done && (
            <span className="text-[11px] text-slate-500">
              Runs in the background — keep chatting, I'll bring the results back.
            </span>
          )}
      </div>
    </div>
  );
};


const RunCard: React.FC<{
  action: CapabilityAction; label: string; run: CapabilityRunView; onDismiss: () => void;
}> = ({ action, label, run, onDismiss }) => {
  const { phase } = run;
  const live = phase === 'starting' || phase === 'running';
  const v = runProgressView(phase === 'starting' ? null : run.progress, run.pinned, run.pinnedCount);
  return (
    <div
      className="mt-3 rounded border border-line bg-white p-4 flex flex-col gap-3"
      data-testid="capability-card"
      data-capability={action.capability}
      data-run-phase={phase}
    >
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-accent ring-4 ring-accent-ring" aria-hidden="true" />
        <span className="font-mono text-[11px] tracking-[0.12em] text-accent-deep">
          {`${label.toUpperCase()} · ${RUN_EYEBROW[phase]}`}
        </span>
      </div>
      <div className="font-display text-[17px] font-medium">{action.scope_draft.criterion}</div>
      {live && (
        <div className="flex flex-col gap-1.5">
          <div className="h-1.5 w-full overflow-hidden rounded bg-slate-100" aria-hidden="true">
            {v.fraction === null
              ? <div className="h-full w-1/3 rounded bg-accent motion-safe:animate-pulse" />
              : <div className="h-full rounded bg-accent" style={{ width: `${v.fraction * 100}%` }} />}
          </div>
          <p aria-live="polite" data-testid="capability-run-progress" className="text-xs text-slate-700">
            {v.line}
          </p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {run.onGoToTab && (
          <button
            type="button"
            onClick={run.onGoToTab}
            data-testid="capability-goto"
            className="rounded-ctl bg-ink px-3 py-1.5 text-xs font-medium text-ground hover:bg-ink/90"
          >
            Go to the tab
          </button>
        )}
        {live && run.onStop && (
          <button
            type="button"
            onClick={run.onStop}
            disabled={run.stop === 'stopping' || run.stop === 'ended'}
            data-testid="capability-stop"
            className="rounded border border-line bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {run.stop === 'stopping' ? 'Stopping…' : 'Stop'}
          </button>
        )}
        {!live && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid="capability-dismiss"
            aria-label={`Dismiss the ${label} suggestion`}
            className="rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          >
            Dismiss
          </button>
        )}
      </div>
      {live && run.stop === 'error' && (
        <p role="alert" className="text-xs text-red-700">Couldn't stop the run. Try again.</p>
      )}
      <p className="text-[11px] text-slate-500">{RUN_FOOTNOTE[phase]}</p>
    </div>
  );
};

export default CapabilityCard;
